const API_BASE = '/api';

async function apiFetch(endpoint, options = {}) {
  const token = localStorage.getItem('token');
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, config);
  
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
    throw new Error('Session expirée');
  }

  const data = await response.json();
  
  if (!response.ok) {
    const err = new Error(data.error || 'Erreur serveur');
    err.status = response.status;
    err.data = data;
    // Pass overlap data for 409 responses
    if (response.status === 409 && data.overlapping_slots) {
      err.overlapData = data;
    }
    throw err;
  }
  
  return data;
}

export const api = {
  // Auth
  login: (username, password) => apiFetch('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  logout: () => apiFetch('/auth/logout', { method: 'POST', body: '{}' }),
  me: () => apiFetch('/auth/me'),
  getSessions: () => apiFetch('/auth/sessions'),
  getMissingImports: () => apiFetch('/auth/missing-imports'),

  // Tasks
  getTasks: () => apiFetch('/tasks'),
  createTask: (data) => apiFetch('/tasks', { method: 'POST', body: JSON.stringify(data) }),
  updateTask: (id, data) => apiFetch(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTask: (id) => apiFetch(`/tasks/${id}`, { method: 'DELETE' }),

  // News
  getNewsRecipients: () => apiFetch('/news/recipients'),
  publishNews: (data) => apiFetch('/news', { method: 'POST', body: JSON.stringify(data) }),
  getPendingNews: () => apiFetch('/news/pending'),
  closeNews: (id) => apiFetch(`/news/${id}/close`, { method: 'POST', body: '{}' }),
  
  // Users
  getUsers: () => apiFetch('/users'),
  getStoreUsers: (storeId) => apiFetch(`/users/store/${storeId}`),
  createUser: (data) => apiFetch('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => apiFetch(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id) => apiFetch(`/users/${id}`, { method: 'DELETE' }),
  
  // Stores
  getStores: () => apiFetch('/stores'),
  getStore: (id) => apiFetch(`/stores/${id}`),
  createStore: (data) => apiFetch('/stores', { method: 'POST', body: JSON.stringify(data) }),
  updateStore: (id, data) => apiFetch(`/stores/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteStore: (id) => apiFetch(`/stores/${id}`, { method: 'DELETE' }),
  
  // Slots
  getSlots: (storeId, startDate, endDate) => apiFetch(`/slots?store_id=${storeId}&start_date=${startDate}&end_date=${endDate}`),
  createSlot: (data) => apiFetch('/slots', { method: 'POST', body: JSON.stringify(data) }),
  updateSlot: (id, data) => apiFetch(`/slots/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSlot: (id) => apiFetch(`/slots/${id}`, { method: 'DELETE' }),
  duplicateDay: (data) => apiFetch('/slots/duplicate-day', { method: 'POST', body: JSON.stringify(data) }),
  duplicateWeek: (data) => apiFetch('/slots/duplicate-week', { method: 'POST', body: JSON.stringify(data) }),
  clearDay: (data) => apiFetch('/slots/clear-day', { method: 'POST', body: JSON.stringify(data) }),
  clearWeek: (data) => apiFetch('/slots/clear-week', { method: 'POST', body: JSON.stringify(data) }),
  getHours: (storeId, startDate, endDate) => apiFetch(`/slots/hours?store_id=${storeId}&start_date=${startDate}&end_date=${endDate}`),
  getPlanningTest: (storeId, startDate, endDate) => apiFetch(`/planning-test?${storeId ? `store_id=${storeId}&` : ''}start_date=${startDate}&end_date=${endDate}`),
  
  // Notes
  getNotes: (storeId, startDate, endDate) => apiFetch(`/notes?store_id=${storeId}&start_date=${startDate}&end_date=${endDate}`),
  getDayNotes: (storeId, date) => apiFetch(`/notes?store_id=${storeId}&date=${date}`),
  createNote: (data) => apiFetch('/notes', { method: 'POST', body: JSON.stringify(data) }),
  updateNote: (id, content) => apiFetch(`/notes/${id}`, { method: 'PUT', body: JSON.stringify({ content }) }),
  deleteNote: (id) => apiFetch(`/notes/${id}`, { method: 'DELETE' }),
  
  // Objectives
  getObjectives: (storeId, month, year) => apiFetch(`/notes/objectives?store_id=${storeId}&month=${month}&year=${year}`),
  getAllObjectives: (storeId) => apiFetch(`/notes/objectives/all?store_id=${storeId}`),
  saveObjectives: (data) => apiFetch('/notes/objectives', { method: 'POST', body: JSON.stringify(data) }),

  // KPI
  kpiImport: async (storeId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('store_id', storeId);
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/kpi/import?store_id=${encodeURIComponent(storeId)}`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Erreur import');
    return data;
  },
  kpiMonthly: (storeId, months) => apiFetch(`/kpi/monthly?store_id=${storeId}&months=${months || 48}`),
  kpiDaily: (storeId, month, year) => apiFetch(`/kpi/daily?store_id=${storeId}&month=${month}&year=${year}`),
  kpiDailyRange: (storeId, start, end) => apiFetch(`/kpi/daily-range?store_id=${storeId}&start=${start}&end=${end}`),
  kpiDailyInput: (storeId, month, year) => apiFetch(`/kpi/daily-input?store_id=${storeId}&month=${month}&year=${year}`),
  kpiDailyInputSave: (data) => apiFetch('/kpi/daily-input', { method: 'POST', body: JSON.stringify(data) }),
  kpiDailyInputDelete: (id) => apiFetch(`/kpi/daily-input/${id}`, { method: 'DELETE' }),
  kpiImports: (storeId) => apiFetch(`/kpi/imports?store_id=${storeId}`),
  kpiDeleteMonth: (storeId, month, year) => apiFetch(`/kpi/month?store_id=${storeId}&month=${month}&year=${year}`, { method: 'DELETE' }),
  adminKpiDailyRanks: (storeId) => apiFetch(`/kpi/admin/daily-ranks?store_id=${storeId}`),

  // DLUO
  dluoGetAll: (storeId) => apiFetch(`/dluo?store_id=${storeId}`),
  dluoCreate: (data) => apiFetch('/dluo', { method: 'POST', body: JSON.stringify(data) }),
  dluoUpdate: (id, data) => apiFetch(`/dluo/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  dluoDelete: (id, reason) => apiFetch(`/dluo/${id}`, { method: 'DELETE', body: JSON.stringify({ reason }) }),
  dluoHistory: (storeId) => apiFetch(`/dluo/history?store_id=${storeId}`),

  // Holidays
  getHolidays: (storeId, start, end) => apiFetch(`/holidays?store_id=${storeId}${start ? `&start=${start}&end=${end}` : ''}`),
  createHoliday: (data) => apiFetch('/holidays', { method: 'POST', body: JSON.stringify(data) }),
  deleteHoliday: (id) => apiFetch(`/holidays/${id}`, { method: 'DELETE' }),

  // Chat
  chatRooms: () => apiFetch('/chat/rooms'),
  chatMessages: (roomId, before) => apiFetch(`/chat/rooms/${roomId}/messages${before ? `?before=${before}` : ''}`),
  chatMembers: (roomId) => apiFetch(`/chat/rooms/${roomId}/members`),
  chatCreateRoom: (data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('member_ids', JSON.stringify(data.member_ids || []));
    if (data.icon) formData.append('icon', data.icon);
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE}/chat/rooms`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData }).then(r => { if (!r.ok) throw new Error('Erreur'); return r.json(); });
  },
  chatUpdateRoom: (id, data) => apiFetch(`/chat/rooms/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  chatDeleteRoom: (id) => apiFetch(`/chat/rooms/${id}`, { method: 'DELETE' }),
  chatAddMembers: (roomId, userIds) => apiFetch(`/chat/rooms/${roomId}/members`, { method: 'POST', body: JSON.stringify({ user_ids: userIds }) }),
  chatRemoveMember: (roomId, userId) => apiFetch(`/chat/rooms/${roomId}/members/${userId}`, { method: 'DELETE' }),
  chatToggleVisibility: (roomId, userId, visible) => apiFetch(`/chat/rooms/${roomId}/members/${userId}/visibility`, { method: 'PUT', body: JSON.stringify({ visible }) }),
  chatUnread: () => apiFetch('/chat/unread'),
  chatUpload: async (file, roomId) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/chat/upload?room_id=${encodeURIComponent(roomId)}`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Erreur upload');
    return data;
  },
};
